<template>
  <page>
    <template #nav-bar>
      <page-nav-bar :items="breadcrumbs" />
    </template>
    <template #header-bar>
      <page-header-bar>
        <span class="pl-3">Review Settings</span>
        <template v-if="newReview" #actions>
          <div class="d-flex pr-4">
            <action-button class="mr-4" @click="onCancel">
              Cancel
            </action-button>
            <action-button color="primary" @click="onStartReview">
              Start Review
            </action-button>
          </div>
        </template>
      </page-header-bar>
    </template>
    <page-tabs use-tab-routes :tabs="tabs">
      <template v-if="review">
        <page-tab-panel>
          <review-form
            v-model="review"
            class="pa-10 flex-grow-1"
            :current-researcher="researcher"
            @update="save"
          />
        </page-tab-panel>
        <page-tab-panel>
          <review-member-settings-controller
            class="pt-10 px-10 pb-6"
            :review="review"
            :current-researcher="researcher"
          />
          <consensus-settings-controller
            class="px-10 pb-6"
            :review="review"
            :current-researcher="researcher"
          />
        </page-tab-panel>
        <page-tab-panel>
          <review-tags-settings-controller
            class="pa-10"
            :review="review"
          />
        </page-tab-panel>
        <page-tab-panel>
          <review-criteria-settings-controller
            class="pa-10"
            :review="review"
          />
        </page-tab-panel>
        <page-tab-panel>
          <review-data-extraction-settings-controller
            :review="review"
          />
        </page-tab-panel>
      </template>
    </page-tabs>
    <confirmation-dialog
      v-if="review"
      v-model="showDeleteConfirmation"
      :name="review.title || ''"
      @confirm="onDeleteConfirm"
    />
  </page>
</template>

<script>
import Page from '@/components/Common/Page';
import PageNavBar from '@/components/Common/PageNavBar';
import PageHeaderBar from '@/components/Common/PageHeaderBar';
import PageTabs from '@/components/Common/PageTabs';
import PageTabPanel from '@/components/Common/PageTabPanel';
import ActionButton from '@/components/Common/ActionButton';
import ReviewForm from '@/components/ReviewForm';
import ReviewMemberSettingsController from '@/components/ReviewSettingsPage/ReviewMemberSettingsController';
import ConsensusSettingsController from '@/components/ReviewSettingsPage/ConsensusSettingsController';
import ReviewTagsSettingsController from '@/components/ReviewSettingsPage/ReviewTagsSettingsController';
import ConfirmationDialog from '@/components/ReviewsPage/ConfirmationDialog';
import ReviewCriteriaSettingsController from '@/components/ReviewSettingsPage/ReviewCriteriaSettingsController';
import ReviewDataExtractionSettingsController from '@/components/ReviewSettingsPage/ReviewDataExtractionSettingsController';
import {
  GetReviewForSettingsPage,
  GetCurrentResearcher,
} from '@/graphql';
import { Review, Researcher } from '@/models';
import { mapActions } from 'vuex';

export default {
  name: 'ReviewSettingsPage',
  components: {
    Page,
    PageNavBar,
    PageHeaderBar,
    PageTabs,
    PageTabPanel,
    ActionButton,
    ReviewForm,
    ReviewMemberSettingsController,
    ConsensusSettingsController,
    ConfirmationDialog,
    ReviewCriteriaSettingsController,
    ReviewTagsSettingsController,
    ReviewDataExtractionSettingsController,
  },
  data: () => ({
    tabs: [
      'Details',
      'Review Team',
      'Tags',
      'Inclusion/Exclusion',
      'Data Extraction',
    ],
    review: null,
    researcher: null,
    showDeleteConfirmation: false,
  }),
  apollo: {
    review: {
      query: GetReviewForSettingsPage,
      variables() {
        return { id: this.$route.params.reviewId };
      },
      update: (data) => new Review(data.review),
      error() {
        // Redirect back to the reviews page on failure to fetch.
        this.$router.push('/reviews');
      },
    },
    researcher: {
      query: GetCurrentResearcher,
      update: (data) => new Researcher(data.currentResearcher),
    },
  },
  computed: {
    breadcrumbs() {
      const review = this.review ?? {};
      return [
        {
          icon: 'home',
          text: 'Reviews',
          disabled: false,
          href: '/reviews',
        },
        {
          text: review.title,
          href: `/review/${review.id}`,
          disabled: false,
          cypressData: 'back-button',
        },
        {
          text: 'Review Settings',
          href: `/review/${review.id}/settings`,
          disabled: false,
        },
      ];
    },
    newReview() {
      return this.$route.query.new;
    },
    reviewIsValid() {
      return this.review.title && this.review.title.trim().length > 0;
    },
  },
  methods: {
    ...mapActions({
      saveReview: 'review/saveReview',
      deleteReview: 'review/deleteReview',
    }),
    async save() {
      return await this.saveReview({
        review: this.review,
        researcher: this.researcher,
      });
    },
    onCancel() {
      this.showDeleteConfirmation = true;
    },
    onStartReview() {
      this.$router.push(`/review/${this.review.id}`);
    },
    async onDeleteConfirm() {
      await this.deleteReview(this.review);
      this.$router.push('/reviews');
    },
  },
};
</script>
